/**
 * API client.
 *
 * Holds the JWT pair in localStorage and refreshes it transparently: a 401 on
 * any call triggers one refresh attempt and one replay, so a long analysis
 * session never dumps the user back to the login screen mid-run.
 */

const ACCESS_KEY = "evolog.access";
const REFRESH_KEY = "evolog.refresh";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly body: unknown,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh?: string) {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** Pull the most useful sentence out of a DRF error body. */
function describe(status: number, body: unknown): string {
  if (typeof body === "string" && body) return body;
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    for (const key of ["detail", "non_field_errors", "message"]) {
      const value = record[key];
      if (typeof value === "string") return value;
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    }
    const [field, value] = Object.entries(record)[0] ?? [];
    if (field) {
      const text = Array.isArray(value) ? value[0] : value;
      return `${field}: ${String(text)}`;
    }
  }
  if (status === 401) return "Sesi berakhir. Silakan masuk kembali.";
  if (status === 403) return "You do not have permission for this action.";
  if (status === 404) return "Not found.";
  return `Permintaan gagal (${status}).`;
}

let refreshing: Promise<boolean> | null = null;

async function refreshAccess(): Promise<boolean> {
  // Collapse concurrent 401s onto one refresh — several panels load at once.
  if (refreshing) return refreshing;
  const refresh = tokens.refresh;
  if (!refresh) return false;
  refreshing = (async () => {
    try {
      const response = await fetch("/api/auth/refresh/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      tokens.set(data.access, data.refresh);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
};

async function send<T>(path: string, options: RequestOptions, retry = true): Promise<T> {
  const headers: Record<string, string> = {};
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(path, {
    method: options.method ?? "GET",
    headers,
    body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    signal: options.signal,
  });

  if (response.status === 401 && retry && (await refreshAccess())) {
    return send<T>(path, options, false);
  }
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed = text ? safeJson(text) : null;
  if (!response.ok) {
    if (response.status === 401) tokens.clear();
    throw new ApiError(response.status, describe(response.status, parsed ?? text), parsed);
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function withQuery(path: string, params?: Record<string, unknown>): string {
  if (!params) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export const api = {
  get: <T,>(path: string, params?: Record<string, unknown>, signal?: AbortSignal) =>
    send<T>(withQuery(path, params), { signal }),
  post: <T,>(path: string, body?: unknown) => send<T>(path, { method: "POST", body }),
  patch: <T,>(path: string, body?: unknown) => send<T>(path, { method: "PATCH", body }),
  put: <T,>(path: string, body?: unknown) => send<T>(path, { method: "PUT", body }),
  delete: <T,>(path: string) => send<T>(path, { method: "DELETE" }),
  upload: <T,>(path: string, formData: FormData) => send<T>(path, { method: "POST", formData }),
};

/** DRF pagination envelope. */
export type Page<T> = { count: number; next: string | null; results: T[] };

/**
 * Batas jumlah halaman yang ditelusuri satu daftar. Dengan PAGE_SIZE 100 di
 * server, ini 20.000 baris — jauh di atas daftar terpanjang yang masuk akal di
 * layar mana pun, dan tetap batas: server yang memulangkan `next` yang sama
 * berulang-ulang akan menggantung tab, bukan memulangkan galat.
 */
const MAX_PAGES = 200;

/**
 * Seluruh baris sebuah daftar, bukan halaman pertamanya saja.
 *
 * Sebelumnya fungsi ini mengambil `results` dan membuang `next`. Selama proyek
 * terbesar berisi 20 sumur tidak ada yang menyentuh batasnya; pada lapangan
 * 1.400 sumur, pohon sumur dan pemilih sumur berhenti di angka seratus tanpa
 * mengatakan apa pun — daftar yang terpotong diam-diam terlihat persis seperti
 * daftar yang lengkap.
 *
 * `next` dari DRF adalah URL utuh. Yang dipakai kembali hanya path dan
 * kuerinya: di balik proxy, skema dan host yang ditulis server belum tentu
 * skema dan host yang dipakai peramban, dan URL utuh itu bisa menyeberang
 * origin justru pada pemasangan yang paling tidak diuji.
 */
/**
 * Berapa halaman diminta sekaligus. Peramban sendiri membatasi sekitar enam
 * sambungan per host, jadi angka yang lebih besar hanya mengantre di tempat
 * lain; yang lebih kecil membuang paralelisme yang sudah tersedia gratis.
 */
const CONCURRENCY = 6;

export const unpage = async <T,>(
  page: Page<T> | T[],
  signal?: AbortSignal,
): Promise<T[]> => {
  if (Array.isArray(page)) return page;
  const rows = [...(page?.results ?? [])];
  const next = page?.next ?? null;
  if (!next) return rows;

  const first = new URL(next, window.location.origin);
  const limit = Number(first.searchParams.get("limit"));
  const start = Number(first.searchParams.get("offset"));
  const count = page?.count ?? 0;

  /* Jalur berurutan dipertahankan sebagai cadangan. Ia satu-satunya yang bisa
     dipakai ketika server tidak menyebut `limit`/`offset` pada `next`-nya —
     tanpa keduanya tidak ada cara menghitung alamat halaman berikutnya tanpa
     lebih dulu menerima halaman sebelumnya. */
  if (!Number.isFinite(limit) || limit <= 0 || !Number.isFinite(start)) {
    let following: string | null = next;
    for (let hop = 0; following && hop < MAX_PAGES; hop++) {
      const url = new URL(following, window.location.origin);
      const body = await api.get<Page<T>>(url.pathname + url.search, undefined, signal);
      rows.push(...(body?.results ?? []));
      following = body?.next ?? null;
    }
    return rows;
  }

  /* `count` sudah ada di halaman pertama, jadi seluruh alamat sisanya dapat
     dihitung sekarang dan diminta bersamaan. Sebelumnya tiap halaman menunggu
     halaman sebelumnya tiba: pada lapangan 1.402 sumur itu lima belas
     perjalanan bolak-balik berurutan, dan antarmukanya tidak menjawab apa pun
     selama itu berlangsung.

     MAX_PAGES tetap membatasi, dan kini membatasi sesuatu yang lebih tegas
     daripada dahulu: dahulu ia menghentikan penelusuran yang tidak
     berkesudahan, sekarang ia juga menahan `count` yang mustahil agar tidak
     berubah menjadi jutaan permintaan sekaligus. */
  const offsets: number[] = [];
  for (let at = start; at < count && offsets.length < MAX_PAGES; at += limit) {
    offsets.push(at);
  }

  const address = (at: number) => {
    const url = new URL(first.toString());
    url.searchParams.set("offset", String(at));
    return url.pathname + url.search;
  };

  for (let from = 0; from < offsets.length; from += CONCURRENCY) {
    const batch = await Promise.all(
      offsets.slice(from, from + CONCURRENCY)
        .map((at) => api.get<Page<T>>(address(at), undefined, signal)),
    );
    // Digabung menurut urutan alamatnya, bukan urutan kedatangannya: yang
    // dipulangkan fungsi ini adalah daftar, dan daftar punya urutan.
    for (const body of batch) rows.push(...(body?.results ?? []));
  }
  return rows;
};
